import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "../../../../api/httpClient";
import Navbar from "../../../../globals/components/Navbar";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Alert,
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import Select from "react-select";
import { toast } from "react-toastify";
import { updateBondNumber } from "../../../../globals/utils/commons";
import { NumericFormat } from "react-number-format";
import DownloadIcon from "@mui/icons-material/Download";
import {
  buildRiderApprovePayload,
  enrichMultiPrincipalNameChangeFields,
  resolveEndorsementFieldsForRider,
  normalizeEndorsementLogsResponse,
  parseCurrency,
  resolveRiderPatchCounty,
  resolvePrincipalIdForPrincipalRider,
} from "../riderHelpers";
import { extractErrorMessage } from "../../../../globals/utils/error-handler";
import {
  LEGACY_VA_ENDORSEMENT_TYPE_OPTIONS,
  RIDER_BOND_KIND,
  RIDER_PAGE_CONFIG,
} from "../riderPageConfig";

const AMOUNT_TYPES = new Set(["Increase", "Decrease"]);

function isBondNumberMissing(bondData) {
  if (!bondData?.bond_number) return true;
  if (typeof bondData.bond_number !== "object") return false;
  return !bondData.bond_number?.bondno_id && !bondData.bond_number?.bond_number;
}

function renderAmountFields({ endorsementFields, setEndorsementFields }) {
  return (
    <>
      <NumericFormat
        customInput={TextField}
        label="From Amount"
        name="from"
        value={endorsementFields.from}
        onValueChange={(values) =>
          setEndorsementFields((prev) => ({
            ...prev,
            from: values.value
              ? Number(values.value).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 2,
                })
              : "",
          }))
        }
        thousandSeparator
        isNumericString
        prefix="$"
        decimalScale={2}
        fixedDecimalScale
        allowNegative={false}
        sx={{ flex: 1 }}
      />
      <NumericFormat
        customInput={TextField}
        label="To Amount"
        name="to"
        value={endorsementFields.to}
        onValueChange={(values) =>
          setEndorsementFields((prev) => ({
            ...prev,
            to: values.value
              ? Number(values.value).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 2,
                })
              : "",
          }))
        }
        thousandSeparator
        isNumericString
        prefix="$"
        decimalScale={2}
        fixedDecimalScale
        allowNegative={false}
        sx={{ flex: 1 }}
      />
    </>
  );
}

function renderCountyChangeFields({
  endorsementFields,
  counties,
  loadingCounties,
  setEndorsementFields,
}) {
  return (
    <>
      <Autocomplete
        options={counties}
        loading={loadingCounties}
        value={
          endorsementFields.endorsement_from_county
            ? counties.find(
                (c) => c.value === endorsementFields.endorsement_from_county
              ) || null
            : null
        }
        onChange={(ev, selected) =>
          setEndorsementFields((prev) => ({
            ...prev,
            endorsement_from_county: selected ? selected.value : "",
            from: selected ? selected.value : "",
          }))
        }
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.label
        }
        isOptionEqualToValue={(option, value) =>
          option && value && option.value === value.value
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="From County"
            margin="dense"
            slotProps={{ input: { ...params.InputProps,
              endAdornment: (
                <>
                  {loadingCounties ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}}
          />
        )}
        sx={{ flex: 1 }}
      />
      <Autocomplete
        options={counties}
        loading={loadingCounties}
        value={
          endorsementFields.endorsement_to_county
            ? counties.find(
                (c) => c.value === endorsementFields.endorsement_to_county
              ) || null
            : null
        }
        onChange={(ev, selected) =>
          setEndorsementFields((prev) => ({
            ...prev,
            endorsement_to_county: selected ? selected.value : "",
            to: selected ? selected.value : "",
          }))
        }
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.label
        }
        isOptionEqualToValue={(option, value) =>
          option && value && option.value === value.value
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="To County"
            margin="dense"
            slotProps={{ input: { ...params.InputProps,
              endAdornment: (
                <>
                  {loadingCounties ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}}
          />
        )}
        sx={{ flex: 1 }}
      />
    </>
  );
}

function renderNamePrincipalFields({
  namePrincipalLabels,
  endorsementFields,
  setEndorsementFields,
}) {
  return (
    <>
      <TextField
        label={namePrincipalLabels.from}
        name="from"
        value={endorsementFields.from}
        onChange={(ev) =>
          setEndorsementFields((prev) => ({
            ...prev,
            from: ev.target.value,
          }))
        }
        sx={{ flex: 1 }}
      />
      <TextField
        label={namePrincipalLabels.to}
        name="to"
        value={endorsementFields.to}
        onChange={(ev) =>
          setEndorsementFields((prev) => ({
            ...prev,
            to: ev.target.value,
          }))
        }
        sx={{ flex: 1 }}
      />
    </>
  );
}

function renderEndorsementValueFields(props) {
  const { endorsementFields, riderCfg } = props;
  if (AMOUNT_TYPES.has(endorsementFields.type)) {
    return renderAmountFields(props);
  }
  if (!riderCfg.nameChangeTypes.includes(endorsementFields.type)) {
    return null;
  }
  if (endorsementFields.type === "Name_Change_County") {
    return renderCountyChangeFields(props);
  }
  return renderNamePrincipalFields(props);
}

export default function BondRiderForm({ bondKind }) {
  const riderCfg = RIDER_PAGE_CONFIG[bondKind];
  const { bond_id } = useParams();
  const location = useLocation();
  const riderEndorsementDraft = location.state?.riderEndorsementDraft;
  const navigate = useNavigate();

  const [bondData, setBondData] = useState(null);
  const [bondNumberOptions, setBondNumberOptions] = useState([]);
  const [form, setForm] = useState(() => ({ ...riderCfg.emptyForm }));

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bondNumberAssigned, setBondNumberAssigned] = useState(false);
  const [counties, setCounties] = useState([]);
  const [state, setState] = useState(6);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [endorsementLogs, setEndorsementLogs] = useState([]);
  const [endorsementDocument, setEndorsementDocument] = useState(null);

  const [endorsementFields, setEndorsementFields] = useState({
    date: "",
    type: "",
    from: "",
    to: "",
    endorsement_from_county: "",
    endorsement_to_county: "",
    effective: "",
    signed_date: "",
  });

  useEffect(() => {
    const rCfg = RIDER_PAGE_CONFIG[bondKind];
    async function fetchData() {
      setIsLoading(true);
      try {
        const data = await apiClient(rCfg.retrieve(bond_id));
        setBondData(data);
        if (data.bond_number) setBondNumberAssigned(true);
      } catch {
        toast.error("Failed to fetch bond data.");
      } finally {
        setIsLoading(false);
      }
    }
    async function fetchEndorsementLogs() {
      setIsLoading(true);
      try {
        const response = await apiClient(`/endorsement-logs/${bond_id}/`);
        setEndorsementLogs(normalizeEndorsementLogsResponse(response));
      } catch (error) {
        toast.error("Failed to load endorsement history");
        if (rCfg.logEndorsementLogError) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      } finally {
        setIsLoading(false);
      }
    }
    fetchEndorsementLogs();
    fetchData();
  }, [bond_id, bondKind]);

  useEffect(() => {
    if (!bond_id) return;
    const fetchEndorsementDocument = async () => {
      try {
        const res = await apiClient(
          `/endorsement-document/?bond_id=${bond_id}`,
          "GET"
        );
        setEndorsementDocument(
          res?.endorsement_document_link != null ? res : null
        );
      } catch {
        setEndorsementDocument(null);
      }
    };
    fetchEndorsementDocument();
  }, [bond_id]);

  useEffect(() => {
    async function fetchBondNumbers() {
      try {
        const response = await apiClient("/bondnumbers/", "GET");
        setBondNumberOptions(
          response.map((bond) => ({
            label: bond.bond_number,
            value: bond.bond_number,
            ...bond,
          }))
        );
      } catch {
        toast.error("Failed to fetch bond numbers.");
      }
    }
    fetchBondNumbers();
  }, []);

  useEffect(() => {
    if (!bondData || bondNumberOptions.length === 0) return;

    const selectedBondNumber = bondNumberOptions.find(
      (option) =>
        option.value === bondData.bond_number?.bond_number ||
        option.value === bondData.bond_number?.value ||
        option.value === bondData.bond_number ||
        option.label === bondData.bond_number
    );

    setState(bondData.state || 6);
    setForm(RIDER_PAGE_CONFIG[bondKind].mapBondToForm(bondData, selectedBondNumber));
  }, [bondData, bondNumberOptions, bondKind]);

  useEffect(() => {
    let fields = resolveEndorsementFieldsForRider(
      bondData,
      endorsementLogs,
      riderEndorsementDraft
    );
    fields = enrichMultiPrincipalNameChangeFields(
      fields,
      bondData,
      endorsementLogs[0],
      riderCfg.principalRiderMeta
    );
    setEndorsementFields(fields);
  }, [bondData, endorsementLogs, riderEndorsementDraft, riderCfg.principalRiderMeta]);

  useEffect(() => {
    const fetchCounties = async () => {
      if (state) {
        setLoadingCounties(true);
        try {
          const countyResponse = await apiClient(
            `/counties-by-state/${state}/`,
            "GET"
          );
          const formattedCounties = countyResponse.map((cty) => ({
            label: cty.bond_county,
            value: cty.bond_county,
          }));
          setCounties(formattedCounties);
        } catch (error) {
          if (RIDER_PAGE_CONFIG[bondKind].logCountyFetchError) {
            // eslint-disable-next-line no-console
            console.error("Error fetching counties:", error);
          }
        } finally {
          setLoadingCounties(false);
        }
      }
    };
    fetchCounties();
  }, [state, bondKind]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEndorsementFieldChange = (e) => {
    setEndorsementFields({
      ...endorsementFields,
      [e.target.name]: e.target.value,
    });
  };

  const handleBondNumberChange = (selected) => {
    setForm({ ...form, bond_number: selected });
  };

  const bondNumberMissing = isBondNumberMissing(bondData);
  const canAssignBondNumberFromRider =
    bondNumberMissing && Boolean(form.bond_number?.bondno_id);
  const releaseBlockedByBondNumber =
    bondNumberMissing && !form.bond_number?.bondno_id;
  const releaseBlockedNoEndorsementContext =
    endorsementLogs.length === 0 && !endorsementFields.type?.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    let activeBondData = bondData;

    if (isBondNumberMissing(activeBondData)) {
      const bondnoId = form.bond_number?.bondno_id;
      if (!bondnoId) {
        toast.error(
          "Select an unused bond number below, or assign one on the main bond page before releasing a rider."
        );
        setIsSubmitting(false);
        return;
      }
      try {
        const usedBy =
          activeBondData?.law_firm?.name ||
          activeBondData?.guest_lawfirm_name ||
          form.trustName ||
          "Rider release";
        await updateBondNumber(Number(bondnoId), Number(bond_id), usedBy);
        activeBondData = await apiClient(riderCfg.retrieve(bond_id));
        setBondData(activeBondData);
        setBondNumberAssigned(true);
        if (isBondNumberMissing(activeBondData)) {
          toast.error(
            "Bond number could not be linked to this bond. Choose another unused number."
          );
          setIsSubmitting(false);
          return;
        }
      } catch (error) {
        const message =
          error.response?.data?.error ||
          "Failed to assign bond number. Pick an unused number or assign on the main bond page.";
        toast.error(message);
        setIsSubmitting(false);
        return;
      }
    }

    if (endorsementLogs.length === 0 && !endorsementFields.type?.trim()) {
      toast.error(
        "Select endorsement type and values, or complete an endorsement request first."
      );
      setIsSubmitting(false);
      return;
    }

    if (
      AMOUNT_TYPES.has(endorsementFields.type) &&
      parseCurrency(endorsementFields.to) == null
    ) {
      toast.error("To amount is required for Increase/Decrease.");
      setIsSubmitting(false);
      return;
    }

    if (!endorsementFields.effective) {
      toast.error("Please fill the Effective Date for Rider.");
      setIsSubmitting(false);
      return;
    }

    const newBondAmount = riderCfg.nameChangeTypes.includes(
      endorsementFields.type
    )
      ? activeBondData?.bond_amount
      : parseCurrency(endorsementFields.to);

    const nested = riderCfg.buildNestedPatch(form, endorsementFields);
    const endorsementLog = endorsementLogs[0];
    const patchPayload = {
      bond_amount: newBondAmount,
      county: resolveRiderPatchCounty(endorsementFields, form.county),
      court_case_number: form.courtCaseNumber,
      ...(typeof riderCfg.patchTopLevelExtras === "function"
        ? riderCfg.patchTopLevelExtras(
            form,
            endorsementFields,
            activeBondData,
            endorsementLog
          )
        : riderCfg.patchTopLevelExtras(form)),
      [riderCfg.nestedKey]: nested,
    };

    const principalMeta = riderCfg.principalRiderMeta;
    const principalIdForRider =
      endorsementFields.type === "Name_Change_Principal" && principalMeta
        ? resolvePrincipalIdForPrincipalRider(
            endorsementLog,
            activeBondData?.[principalMeta.listKey],
            principalMeta
          )
        : undefined;

    try {
      await apiClient(riderCfg.update(bond_id), "PATCH", patchPayload);

      const data = await apiClient(riderCfg.retrieve(bond_id));
      const approvePayload = buildRiderApprovePayload(
        data,
        endorsementFields,
        {
          principalId: principalIdForRider,
          bondKind: riderCfg.bondKind,
          endorsementLog,
        }
      );

      const approveResponse = await apiClient(
        riderCfg.approve(bond_id),
        "POST",
        approvePayload
      );

      const bondStatus =
        approveResponse?.status ?? approveResponse?.bond?.status;
      const docGenStatus =
        approveResponse?.doc_generation_status ??
        approveResponse?.bond?.doc_generation_status;

      if (bondStatus === "Failed" || docGenStatus === "failed") {
        toast.warning(
          "Rider request was accepted but document generation failed. Check bond status or retry shortly."
        );
      } else {
        toast.success("Bond rider released successfully.");
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        toast.error(
          "Document generation is already in progress. Wait a few minutes or contact support."
        );
      } else {
        toast.error(
          extractErrorMessage(error) || "Failed to release bond rider."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !bondData) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  const namePrincipalLabels = riderCfg.resolvePrincipalNameLabels(
    endorsementFields.type
  );

  const selectableTypeValues = new Set(
    riderCfg.typeOptions.map((opt) => opt.value)
  );
  const legacyVaTypeOptions =
    bondKind === RIDER_BOND_KIND.va &&
    endorsementFields.type &&
    !selectableTypeValues.has(endorsementFields.type)
      ? LEGACY_VA_ENDORSEMENT_TYPE_OPTIONS.filter(
          (opt) => opt.value === endorsementFields.type
        )
      : [];

  return (
    <>
      <Navbar />

      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          mt: 2,
          mb: 2,
          p: 4,
          background: "#fafbfc",
          borderRadius: 2,
          boxShadow: "0 2px 8px #0001",
        }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h5" fontWeight="bold">
            {riderCfg.pageTitle}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            {endorsementDocument?.endorsement_document_link && (
              <Button
                variant="outlined"
                color="primary"
                sx={{ px: 1, py: 1 }}
                startIcon={<DownloadIcon />}
                onClick={() =>
                  window.open(
                    endorsementDocument.endorsement_document_link,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                View Endorsement Document
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(riderCfg.bondDetailPath(bond_id))}
              sx={{ px: 1, py: 1 }}
            >
              View all bond details
            </Button>
          </Box>
        </Box>

        <Box mb={2}>
          <Typography fontWeight="bold" mb={2}>
            Endorsement Change Details
          </Typography>
          {endorsementLogs.length === 0 && !endorsementFields.type && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No endorsement history was found for this bond. Complete an
              endorsement request first, or enter rider details manually below.
            </Alert>
          )}
          {endorsementLogs.length === 0 && endorsementFields.type && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Endorsement details were prefilled from the endorsement dialog.
              Submit an endorsement request with a court order to save
              endorsement history on this bond.
            </Alert>
          )}
          <Box display="flex" gap={2} mb={2} mt={2}>
            <TextField
              label="Last Date of Change"
              type="date"
              name="date"
              value={endorsementFields.date}
              onChange={handleEndorsementFieldChange}
              slotProps={{ inputLabel: { shrink: true }}}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Type"
              select
              fullWidth
              value={endorsementFields.type}
              onChange={(ev) =>
                setEndorsementFields((prev) => ({
                  ...prev,
                  type: ev.target.value,
                }))
              }
              slotProps={{
                select: { native: true },
                inputLabel: { shrink: true },
              }}
              sx={{ flex: 1 }}
            >
              <option value="" disabled hidden />
              {legacyVaTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value} disabled>
                  {opt.label} (legacy — select a supported type to release)
                </option>
              ))}
              {riderCfg.typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </TextField>
          </Box>
          <Box display="flex" gap={2} mb={2}>
            {renderEndorsementValueFields({
              endorsementFields,
              riderCfg,
              namePrincipalLabels,
              setEndorsementFields,
              counties,
              loadingCounties,
            })}
          </Box>
          <TextField
            label="Effective Date"
            type="date"
            name="effective"
            value={endorsementFields.effective}
            onChange={handleEndorsementFieldChange}
            slotProps={{ inputLabel: {
              shrink: true,
              sx: { fontWeight: "bold" },
            }}}
            sx={{ mb: 2 }}
            fullWidth
          />
          <TextField
            label="Signed Date"
            type="date"
            name="signed_date"
            value={endorsementFields.signed_date}
            onChange={handleEndorsementFieldChange}
            slotProps={{ inputLabel: { shrink: true }}}
            sx={{ mb: 2 }}
            fullWidth
          />
        </Box>

        <Typography fontWeight="bold" mb={1}>
          Bond Review
        </Typography>
        {riderCfg.bondReviewFields.map((field) => (
          <Box mb={2} key={field.name}>
            <TextField
              label={field.label}
              name={field.name}
              fullWidth
              value={form[field.name] ?? ""}
              onChange={handleChange}
              margin="dense"
            />
          </Box>
        ))}
        <Box mb={2}>
          <Autocomplete
            options={counties}
            loading={loadingCounties}
            value={
              form.county
                ? counties.find((c) => c.value === form.county) || null
                : null
            }
            onChange={(ev, selected) =>
              setForm({ ...form, county: selected ? selected.value : "" })
            }
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="County"
                variant="outlined"
                margin="dense"
                slotProps={{ input: { ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCounties ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}}
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <TextField
            label="Court Case Number"
            name="courtCaseNumber"
            fullWidth
            value={form.courtCaseNumber}
            onChange={handleChange}
            margin="dense"
          />
        </Box>
        <Box mb={2}>
          <Select
            options={bondNumberOptions}
            value={form.bond_number}
            onChange={handleBondNumberChange}
            isDisabled={bondNumberAssigned}
            isClearable
            isSearchable
            placeholder="Select Bond Number"
            getOptionLabel={(option) => {
              if (option.is_used) return `${option.label} (Assigned)`;
              return `${option.label}`;
            }}
            getOptionValue={(option) => option.value}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Box>
        {bondNumberMissing && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {canAssignBondNumberFromRider
              ? "This bond has no bond number on file yet. The number you selected will be assigned when you release the rider (must be unused)."
              : "This bond has no assigned bond number. Select an unused number below, or open the main bond page, assign a number, click Update, then return here."}
          </Alert>
        )}
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={
              isSubmitting ||
              releaseBlockedByBondNumber ||
              releaseBlockedNoEndorsementContext
            }
            endIcon={isSubmitting && <CircularProgress size={18} />}
          >
            {isSubmitting ? "Releasing..." : "Release Bond Rider"}
          </Button>
        </Box>
      </Box>
    </>
  );
}

BondRiderForm.propTypes = {
  bondKind: PropTypes.oneOf(Object.values(RIDER_BOND_KIND)).isRequired,
};
